import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon, PlayCircleIcon, ChevronDownIcon } from 'lucide-react';
export function BlueprintHero() {
  const scrollToSections = () => {
    const el = document.getElementById('overview');
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({
        top,
        behavior: 'smooth'
      });
    }
  };
  return <section className="relative w-full min-h-screen bg-[#0a0a0f] flex flex-col items-center justify-center overflow-hidden" data-id="element-541">
      {/* Background layers */}
      <div className="absolute inset-0" data-id="element-542">
        <div className="absolute inset-0 bg-gradient-to-b from-[#1a1a2e]/40 via-[#0a0a0f] to-[#0a0a0f]" data-id="element-543"></div>
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-[#d4a017]/5 rounded-full blur-[200px]" data-id="element-544"></div>
        <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-[#1a1a2e]/30 rounded-full blur-[150px]" data-id="element-545"></div>
      </div>

      {/* Grid pattern overlay */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
      backgroundImage: 'linear-gradient(#d4a017 1px, transparent 1px), linear-gradient(90deg, #d4a017 1px, transparent 1px)',
      backgroundSize: '60px 60px'
    }} data-id="element-546"></div>

      <div className="relative z-10 w-full max-w-5xl mx-auto px-6 pt-24 text-center" data-id="element-547">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 1,
        delay: 0.2
      }} data-id="element-548">
          <p className="text-[#d4a017]/40 tracking-[0.4em] text-[10px] uppercase mb-4 font-light" data-id="element-549">
            Prototype Experience — Partner &amp; Collaborator Knowledge Hub
          </p>
          <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl text-[#f5f0e8] mb-6 tracking-tight leading-[0.9]" data-id="element-550">
            Experience
            <br data-id="element-551" />
            <span className="text-[#d4a017]" data-id="element-552">Blueprint</span>
          </h1>
          <div className="inline-block border border-[#d4a017]/30 bg-[#d4a017]/5 px-4 py-1.5 mb-6" data-id="element-553">
            <p className="text-[#d4a017]/70 text-[10px] tracking-[0.3em] uppercase font-medium" data-id="element-554">
              Prototype Edition
            </p>
          </div>
          <div className="w-20 h-px bg-gradient-to-r from-transparent via-[#d4a017]/40 to-transparent mx-auto mb-8" data-id="element-555"></div>
          <p className="text-lg md:text-xl text-[#f5f0e8]/50 font-light max-w-2xl mx-auto leading-relaxed" data-id="element-556">
            A complete guide to the prototype immersive experience of Life in
            the Sikh Kingdoms — story, portals, sensory design, production
            logic, and stakeholder explainers in one place.
          </p>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.6
      }} className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-557">
          <a href="#video-explainer" className="inline-flex items-center gap-3 bg-[#d4a017] hover:bg-[#b8890f] text-[#0a0a0f] px-7 py-3.5 text-sm tracking-[0.15em] uppercase font-medium transition-colors duration-300" data-id="element-558">
            <PlayCircleIcon className="w-4 h-4" data-id="element-559" />
            Watch Video Explainer
          </a>
          <button onClick={scrollToSections} className="inline-flex items-center gap-3 border border-[#d4a017]/30 hover:border-[#d4a017] text-[#f5f0e8]/70 hover:text-[#d4a017] px-7 py-3.5 text-sm tracking-[0.15em] uppercase font-light transition-all duration-300" data-id="element-560">
            <ChevronDownIcon className="w-4 h-4" data-id="element-561" />
            Explore Sections
          </button>
        </motion.div>
      </div>

      {/* Video Embed Area */}
      <motion.div id="video-explainer" initial={{
      opacity: 0,
      y: 40
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 1,
      delay: 0.8
    }} className="relative z-10 w-full max-w-4xl mx-auto px-6 mt-20 mb-12" data-id="element-562">
        <div className="border border-[#d4a017]/20 bg-[#0f0f23]/80 backdrop-blur-sm" data-id="element-563">
          <div className="aspect-video bg-[#0a0a0f] relative overflow-hidden" data-id="element-564">
            <iframe className="w-full h-full" src="https://www.youtube.com/embed/DFuCbnP0y5g" title="Experience Blueprint — Video Explainer" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen data-id="element-565"></iframe>
          </div>
          <div className="p-6 border-t border-[#d4a017]/10" data-id="element-566">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-567">
              <div data-id="element-568">
                <h3 className="font-serif text-lg text-[#f5f0e8] mb-1" data-id="element-569">
                  The Complete Experience Explainer
                </h3>
                <p className="text-[#f5f0e8]/40 text-sm font-light" data-id="element-570">
                  A walkthrough of the immersive world, portal system, and
                  production vision.
                </p>
              </div>
              <a href="#" className="text-[#d4a017]/60 hover:text-[#d4a017] text-xs tracking-[0.15em] uppercase font-light transition-colors whitespace-nowrap" data-id="element-571">
                View Transcript →
              </a>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Scroll indicator */}
      <motion.div className="absolute bottom-8 left-1/2 -translate-x-1/2" animate={{
      y: [0, 8, 0]
    }} transition={{
      duration: 2.5,
      repeat: Infinity,
      ease: 'easeInOut'
    }} data-id="element-572">
        <div className="w-px h-12 bg-gradient-to-b from-transparent via-[#d4a017]/30 to-transparent" data-id="element-573"></div>
      </motion.div>
    </section>;
}